/**
 * Valor patrimonial de bens conforme campos efetivos do cadastro (drheranca).
 * Alinha cartões da UI, relatório e calculateInventory.
 */

import type { AnyAsset, Investimento, ParticipacaoSocietaria, Seguro } from '../types';

export type AssetValuationPurpose = 'DATA_OBITO' | 'DATA_ESCRITURA';

/** tipos_ativo exclusivos de OutroBem (não confundir com Investimento). */
const OUTRO_BEM_TIPO_ATIVO = new Set<string>([
    'direitos_autorais',
    'joia_quadro_arte',
    'maquinario',
    'semovente',
    'outro',
]);

export function isInvestimentoAsset(asset: AnyAsset): asset is Investimento {
    return (
        'tipo_ativo' in asset &&
        typeof (asset as Investimento).tipo_ativo === 'string' &&
        !OUTRO_BEM_TIPO_ATIVO.has((asset as Investimento).tipo_ativo)
    );
}

/**
 * Valor base do investimento antes de regras de herança (VGBL, etc. tratadas em getAnyAssetPatrimonioValue).
 */
export function getInvestimentoBasePatrimonioValue(inv: Investimento): number {
    const tipo = inv.tipo_ativo;
    if (tipo === 'conta_bancaria') {
        return Number(inv.conta_bancaria_saldo) || 0;
    }
    if (tipo === 'previdencia_privada') {
        return Number(inv.previdencia_saldo) || 0;
    }
    if (tipo === 'consorcio_nao_contemplado') {
        return Number(inv.valor_pago_cotas) || 0;
    }
    const v = inv.valor;
    return typeof v === 'number' ? v : parseFloat(String(v ?? 0)) || 0;
}

export function toParticipacaoMultiplier(pct: unknown): number {
    const n = typeof pct === 'number' ? pct : parseFloat(String(pct ?? ''));
    if (!isFinite(n) || n < 0) return 1;
    return n > 1 ? n / 100 : n;
}

/** Valor patrimonial exibido / usado em somas (participação societária × %). */
export function getParticipacaoSocietariaPatrimonioValue(p: ParticipacaoSocietaria): number {
    const v = typeof p.valor === 'number' ? p.valor : parseFloat(String(p.valor ?? 0)) || 0;
    if (v <= 0) return 0;
    return v * toParticipacaoMultiplier(p.percentual_participacao);
}

/**
 * Valor do seguro para composição do patrimônio (alinhado ao cadastro: resgatável usa resgate líquido).
 */
export function getSeguroBasePatrimonioValue(s: Seguro): number {
    if (s.tipo_seguro === 'resgatavel') {
        const resgate = Number(s.valor_restate_liquido) || 0;
        if (resgate > 0) return resgate;
    }
    return typeof s.valor === 'number' ? s.valor : parseFloat(String(s.valor ?? 0)) || 0;
}

function getAssetBaseValueForPurpose(asset: AnyAsset, purpose: AssetValuationPurpose): number {
    // Imóvel: para atos/escritura, prioriza valor atualizado (valor_historico), com fallback para valor de óbito.
    if ('tipo_imovel' in asset) {
        const valorObito = typeof asset.valor === 'number' ? asset.valor : parseFloat(String(asset.valor ?? 0)) || 0;
        const valorEscritura = typeof asset.valor_historico === 'number'
            ? asset.valor_historico
            : parseFloat(String(asset.valor_historico ?? 0)) || 0;
        if (purpose === 'DATA_ESCRITURA') {
            return valorEscritura > 0 ? valorEscritura : valorObito;
        }
        return valorObito;
    }

    if (isInvestimentoAsset(asset)) {
        return getInvestimentoBasePatrimonioValue(asset);
    }
    if ('tipo_seguro' in asset) {
        return getSeguroBasePatrimonioValue(asset as Seguro);
    }
    if ('valor' in asset) {
        const v = (asset as { valor?: unknown }).valor;
        return typeof v === 'number' ? v : parseFloat(String(v ?? 0)) || 0;
    }
    return 0;
}

/**
 * Valor patrimonial de qualquer bem do inventário (espólio), para cálculos e telas.
 * VGBL e seguro com beneficiários nomeados retornam 0.
 */
export function getAnyAssetPatrimonioValue(asset: AnyAsset): number {
    return getAnyAssetValueForPurpose(asset, 'DATA_OBITO');
}

/**
 * Valor de bem conforme finalidade de cálculo.
 * - DATA_OBITO: prioriza bases de abertura da sucessão (causa mortis).
 * - DATA_ESCRITURA: para atos/simulação, imóvel usa valor atualizado (quando informado).
 */
export function getAnyAssetValueForPurpose(asset: AnyAsset, purpose: AssetValuationPurpose): number {
    if (isInvestimentoAsset(asset) && asset.tipo_ativo === 'previdencia_privada' && asset.tipo_previdencia === 'vgbl') {
        return 0;
    }
    if ('tipo_seguro' in asset && (asset as Seguro).tem_beneficiarios) {
        return 0;
    }

    const valor = getAssetBaseValueForPurpose(asset, purpose);
    if (valor <= 0) return 0;

    const hasParticipacao = 'percentual_participacao' in asset;
    const multiplier = hasParticipacao ? toParticipacaoMultiplier((asset as { percentual_participacao?: unknown }).percentual_participacao) : 1;

    return valor * multiplier;
}
